import { useState } from 'react';
import { SPLITS } from '../data/splits';
import { ACTIVITY_LABEL, nutritionTargets } from '../engine/nutrition';
import { dayShort } from '../lib/dates';
import { useApp } from '../store/AppStore';
import { GOAL_LABEL, type ActivityLevel, type Goal } from '../store/types';

/**
 * First-run setup.
 *
 * Four short screens rather than one long form, and everything is pre-filled so
 * it's mostly confirming rather than typing. Height and weight aren't optional
 * niceties — they feed the calorie maths, and a wrong height skews every number
 * on the Progress screen quietly and forever.
 */
export function Onboarding() {
  const { state, setProfile, setSettings } = useApp();
  const { profile } = state;
  const [step, setStep] = useState(0);

  const toggleDay = (list: 'liftDays' | 'cardioDays', day: number) => {
    const current = profile[list];
    const other = list === 'liftDays' ? 'cardioDays' : 'liftDays';
    setProfile({
      [list]: current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort(),
      [other]: profile[other].filter((d) => d !== day),
    });
  };

  const targets = nutritionTargets(profile);
  const steps = ['You', 'Body', 'Goal', 'Schedule'];
  const isLast = step === steps.length - 1;

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">Iron Ledger</span>
        <span className="meta">
          Step {step + 1} of {steps.length}
        </span>
      </header>

      <div className="stack">
        <div className="segbar">
          {steps.map((s, i) => (
            <span key={s} className={i <= step ? 'on' : ''} />
          ))}
        </div>

        {step === 0 && (
          <div className="card accent">
            <div className="card-title" style={{ color: 'var(--accent)' }}>
              Welcome
            </div>
            <h1>Let's set this up</h1>
            <p className="muted small" style={{ marginTop: 8 }}>
              Four quick screens. Everything stays on this device — there's no account
              and nothing gets sent anywhere. You can change all of it later in
              Settings.
            </p>
            <div className="field" style={{ marginTop: 14 }}>
              <label htmlFor="ob-name">What should I call you?</label>
              <input
                id="ob-name"
                value={profile.name}
                autoFocus
                placeholder="Your name"
                onChange={(e) => setProfile({ name: e.target.value })}
              />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="card">
            <div className="card-title">Your numbers</div>
            <p className="muted small" style={{ marginBottom: 14 }}>
              These drive your calorie and protein targets. Rough is fine, but guessing
              your height throws off every figure downstream, so it's worth getting
              right.
            </p>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="ob-age">Age</label>
                <input
                  id="ob-age"
                  type="number"
                  inputMode="numeric"
                  value={profile.age || ''}
                  onChange={(e) => setProfile({ age: Number(e.target.value) || 0 })}
                />
              </div>
              <div className="field">
                <label htmlFor="ob-sex">Sex</label>
                <select
                  id="ob-sex"
                  value={profile.sex}
                  onChange={(e) => setProfile({ sex: e.target.value as 'male' | 'female' })}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="ob-height">Height (cm)</label>
                <input
                  id="ob-height"
                  type="number"
                  inputMode="numeric"
                  value={profile.heightCm || ''}
                  onChange={(e) => setProfile({ heightCm: Number(e.target.value) || 0 })}
                />
              </div>
              <div className="field">
                <label htmlFor="ob-weight">Weight (kg)</label>
                <input
                  id="ob-weight"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={profile.bodyweightKg || ''}
                  onChange={(e) => setProfile({ bodyweightKg: Number(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="card">
            <div className="card-title">What are you after?</div>
            <div className="stack">
              <div className="field">
                <label htmlFor="ob-goal">Goal</label>
                <select
                  id="ob-goal"
                  value={profile.goal}
                  onChange={(e) => setProfile({ goal: e.target.value as Goal })}
                >
                  {(Object.keys(GOAL_LABEL) as Goal[]).map((g) => (
                    <option key={g} value={g}>
                      {GOAL_LABEL[g]}
                    </option>
                  ))}
                </select>
                <div className="hint">
                  Sets your calorie target, which cardio gets suggested, and whether
                  lifting days get a conditioning finisher.
                </div>
              </div>

              <div className="field">
                <label htmlFor="ob-activity">Daily activity outside the gym</label>
                <select
                  id="ob-activity"
                  value={profile.activityLevel}
                  onChange={(e) =>
                    setProfile({ activityLevel: e.target.value as ActivityLevel })
                  }
                >
                  {(Object.keys(ACTIVITY_LABEL) as ActivityLevel[]).map((a) => (
                    <option key={a} value={a}>
                      {ACTIVITY_LABEL[a]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid-2">
                <div className="stat">
                  <div className="label">Daily calories</div>
                  <div className="value">
                    {targets.calories}
                    <span className="unit"> kcal</span>
                  </div>
                </div>
                <div className="stat">
                  <div className="label">Protein</div>
                  <div className="value">
                    {targets.proteinG}
                    <span className="unit"> g</span>
                  </div>
                </div>
              </div>
              <div className="tiny faint">
                Estimated from population equations, not measured from you. Use them as
                a starting point and adjust from what the scale actually does over 2–3
                weeks.
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="card">
            <div className="card-title">When do you train?</div>
            <div className="stack">
              <div className="field">
                <label>Lifting days</label>
                <div className="daypicker">
                  {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                    <button
                      key={d}
                      className={profile.liftDays.includes(d) ? 'on' : ''}
                      onClick={() => toggleDay('liftDays', d)}
                    >
                      {dayShort(d)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <label>Cardio days</label>
                <div className="daypicker">
                  {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                    <button
                      key={d}
                      className={profile.cardioDays.includes(d) ? 'cardio-on' : ''}
                      onClick={() => toggleDay('cardioDays', d)}
                    >
                      {dayShort(d)}
                    </button>
                  ))}
                </div>
                <div className="hint">
                  Miss a day and nothing is lost — the planner works off what you've
                  actually trained, not the calendar.
                </div>
              </div>

              <div className="field">
                <label htmlFor="ob-time">Usual gym time</label>
                <input
                  id="ob-time"
                  type="time"
                  value={profile.gymTime}
                  onChange={(e) => setProfile({ gymTime: e.target.value })}
                />
              </div>

              <div className="note small">
                You'll start on the <strong>{SPLITS[0]!.name}</strong> and switch to{' '}
                <strong>{SPLITS[1]!.name}</strong> after four weeks, then keep alternating.
                Change that any time in Settings.
              </div>
            </div>
          </div>
        )}

        <div className="row" style={{ gap: 8 }}>
          {step > 0 && (
            <button className="btn ghost lg" onClick={() => setStep(step - 1)}>
              Back
            </button>
          )}
          <button
            className="btn primary lg grow"
            disabled={step === 1 && (!profile.heightCm || !profile.bodyweightKg || !profile.age)}
            onClick={() => {
              if (isLast) setSettings({ onboarded: true });
              else setStep(step + 1);
            }}
          >
            {isLast ? "Let's go" : 'Continue'}
          </button>
        </div>

        {step === 0 && (
          <button
            className="btn ghost sm block"
            onClick={() => setSettings({ onboarded: true })}
          >
            Skip — I'll set it up later
          </button>
        )}
      </div>
    </div>
  );
}
